import axios from 'axios';
import { Subject } from 'rxjs';
import { IDatabase, MemoryDatabase } from '../database';
import { IFlowyNotification, IGizmoEvent, IPlatformInterface } from '../types';
import { FlowyAnalytics } from '../analytics';
import FlowyFoundation from './flowy-foundation';

interface IConstructor {
  apiUrl: string;
  platformInterface?: IPlatformInterface;
}

/**
 * A single instance of this class will be located on the outer most provider. The objective of
 * this is to host class instances that should be singletos. Like the database and the platform
 * interface.
 */
class FlowyGlobal {
  /**
   * Url to the location of the api
   */
  private _apiUrl: string;
  /**
   * Class with the methods used to interact with the platform layer
   */
  private _platformInterface?: IPlatformInterface;
  /**
   * Database instance, depending on the platform it will be initialized
   */
  private _database?: IDatabase;
  /**
   * Flowy analytics instance
   */
  private _analytics?: FlowyAnalytics;
  /**
   * Notifications stream which will be used by the platform layer to display the
   * appropiate controls
   */
  public notifications: Subject<IFlowyNotification>;
  /**
   * Global events stream which will be used by the core to communicate between RootGizmos,
   * right now it's basically used to communicated 'annex_form'
   */
  public globalEvents: Subject<IGizmoEvent>;
  // TODO: when we have database support we're going to add the Dao
  // private _dao?: Dao;
  private _foundations: { [key: string]: FlowyFoundation };

  constructor({ apiUrl, platformInterface }: IConstructor) {
    this._apiUrl = apiUrl;
    this._platformInterface = platformInterface;
    this._foundations = {};

    this.notifications = new Subject<IFlowyNotification>();
    this.globalEvents = new Subject<IGizmoEvent>();
  }

  public init = (): Promise<FlowyGlobal> => {
    return new Promise<FlowyGlobal>(async resolve => {
      const platformInterface = this.getPlatformInterface();
      axios.interceptors.response.use(
        function(response) {
          return response;
        },
        function(error) {
          if (
            error.response.status === 401 &&
            error.response?.data?.detail === 'No auth'
          ) {
            platformInterface?.logout('session expired').then();
          }

          return Promise.reject(error);
        }
      );

      // Right now by default we're going to use the memory database
      const user = await this._platformInterface?.getUser();
      this._database = new MemoryDatabase({ user });
      await this._database.init();

      this._analytics = new FlowyAnalytics({
        flowyGlobal: this,
      });
      this._analytics.init();

      resolve(this);
    });
  };

  /**
   * Register a FlowyFoundation instance
   *
   * @param {string} fid RootGizmoData fid
   * @param {FlowyFoundation} foundation FlowyFoundation instance
   */
  public registerFoundation = ({
    fid,
    foundation,
  }: IRegisterFoundationParams): void => {
    this._foundations[fid] = foundation;
  };

  /**
   * Get a registered FlowyFoundation instance
   * @param {string} fid RootGizmoData fid
   * @returns
   */
  public getFoundation = ({
    fid,
  }: IGetFoundationParams): FlowyFoundation | undefined => {
    return this._foundations[fid];
  };

  /**
   * Remove a registered FlowyFoundation instance
   *
   * @param {string} fid RootGizmoData fid
   */
  public unregisterFoundation = ({
    fid,
  }: IUnregisterFoundationParams): void => {
    delete this._foundations[fid];
  };

  /**
   * Get the apiUrl
   * @returns {string} _apiUrl
   */
  public getApiUrl = (): string => {
    return this._apiUrl;
  };

  /**
   * Get the database
   * @returns {IDatabase} the global instance of the database
   */
  public getDatabase = (): IDatabase | undefined => {
    return this._database;
  };

  /**
   * Get the global platformInterface instance provided by the platform layer
   * @returns {IPlatformInterface} the global instnace of the platform interface
   */
  public getPlatformInterface = (): IPlatformInterface | undefined => {
    return this._platformInterface;
  };

  /**
   * Get the global analytics instance
   * @returns {FlowyAnalytics} the global instance of the analytics
   */
  public getAnalytics = (): FlowyAnalytics | undefined => {
    return this._analytics;
  };

  /**
   * Send a notification to the platform layer
   * @param {IFlowyNotification} notification notification data
   * @returns
   */
  public sendNotification = ({
    notification,
  }: ISendNotificationParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      this.notifications.next(notification);

      resolve();
    });
  };

  /**
   * Send an event to all active FlowyFoundations which will then send it to the RootGizmo (in
   * case it's the destiny)
   * @param {IGizmoEvent} event Gizmo event that will be send to all active FlowyFoundations
   * @returns
   */
  public sendEvent = ({ event }: ISendEventParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      this.globalEvents.next(event);
      resolve();
    });
  };
}

interface ISendNotificationParams {
  notification: IFlowyNotification;
}

interface ISendEventParams {
  event: IGizmoEvent;
}

interface IRegisterFoundationParams {
  fid: string;
  foundation: FlowyFoundation;
}

interface IGetFoundationParams {
  fid: string;
}

interface IUnregisterFoundationParams {
  fid: string;
}

export default FlowyGlobal;
